
import { createContext, useState, useContext, useEffect, ReactNode } from "react";
import { User } from "@/types";

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, userType: string, isAdmin?: boolean) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  deactivateUser: (userId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // This is a mock login - in a real app, you would connect to a backend
      if (email === "admin@example.com" && password === "password") {
        const userData: User = {
          id: "1",
          email: "admin@example.com",
          name: "Dr. Emma Taylor", // Updated admin name to match employee data
          isAdmin: true,
          userType: "admin",
          role: "admin",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
        console.log("Logged in as admin:", userData);
      } else if (email === "user@example.com" && password === "password") {
        const userData: User = {
          id: "2",
          email: "user@example.com",
          name: "Regular User",
          isAdmin: false,
          userType: "nurse",
          role: "staff",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } else if (email === "doctor@example.com" && password === "password") {
        // Michael Johnson has id: "5"
        const userData: User = {
          id: "5",
          email: "doctor@example.com",
          name: "Michael Johnson",
          isAdmin: false,
          userType: "doctor",
          role: "staff",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
        console.log("Logged in as doctor:", userData);
      } else if (email === "patient1@example.com" && password === "password") {
        const userData: User = {
          id: "10",
          email: "patient1@example.com",
          name: "John Patient",
          isAdmin: false,
          userType: "patient",
          role: "patient",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } else if (email === "patient2@example.com" && password === "password") {
        const userData: User = {
          id: "11",
          email: "patient2@example.com",
          name: "Jane Patient",
          isAdmin: false,
          userType: "patient",
          role: "patient",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } else if (email === "student@example.com" && password === "password") {
        const userData: User = {
          id: "4",
          email: "student@example.com",
          name: "Jane Smith",
          isAdmin: false,
          userType: "student",
          role: "staff",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } else if (email === "nurse@example.com" && password === "password") {
        // Emily Parker has id: "8"
        const userData: User = {
          id: "8",
          email: "nurse@example.com",
          name: "Emily Parker",
          isAdmin: false,
          userType: "nurse",
          role: "staff",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } else if (email === "doctor2@example.com" && password === "password") {
        // Dr. Sarah Johnson has id: "6"
        const userData: User = {
          id: "6",
          email: "doctor2@example.com",
          name: "Dr. Sarah Johnson",
          isAdmin: false,
          userType: "doctor",
          role: "staff",
          isActive: true
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } else {
        throw new Error("Invalid credentials");
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string, userType: string, isAdmin: boolean = false) => {
    setIsLoading(true);
    try {
      // This is a mock registration - in a real app, you would connect to a backend
      const userData: User = {
        id: Math.random().toString(36).substr(2, 9),
        email,
        name,
        isAdmin: isAdmin,
        userType: userType as "admin" | "doctor" | "nurse" | "student" | "other" | "patient",
        role: isAdmin ? "admin" : (userType === "patient" ? "patient" : "staff"),
        isActive: true
      };
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
    } catch (error) {
      console.error("Registration failed:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  const deactivateUser = (userId: string) => {
    // In a real app, this would call a backend API
    // For now, we just simulate the deactivation by logging
    console.log(`User ${userId} has been deactivated`);
    
    // If the current user is being deactivated, log them out
    if (user?.id === userId) {
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading, deactivateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
